/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Pavel Reznak (reznak@cadwork.cz)
 */

// jscs:disable requireCamelCaseOrUpperCaseIdentifiers

import Pass from "App/render/webgl/passes/pass";
import WebGLRenderGraph from "App/render/webgl/render_graph";
import {vec2} from "gl-matrix";
import ShadowMapPoissonShader from "App/render/webgl/shaders/shadow_map/shadow_map_poisson";

import {Containers, Factory} from "App/utils/factory";
import View from "App/view/view";
import {Framebuffer} from "App/utils/frame_buffer";
import {
    OpaqueBinTypeName,
    OpaqueNoTextureBinTypeName
} from "App/render/webgl/nodes/bins/type_names";
import IScene from "js_interface/src/scene_interface";

/**
 * Create SSAO pass : extend Pass
 *
 * @param {SceneAPI.Scene} aSceneAPI
 * @param {WebGLRenderGraph} aRenderGraph
 * @class
 * @extends Pass
 */
export default
class ShadowMapPoisson extends Pass {

    public static TypeName: string = "ShadowMapPoisson";
    private _poissonDisk: any[];
    private _shader: ShadowMapPoissonShader;

    constructor(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph) {
        super(aSceneAPI, aRenderGraph);

        this._poissonDisk = [];

        this.initialize();
    }
    public static create(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph): ShadowMapPoisson {
        return new ShadowMapPoisson(aSceneAPI, aRenderGraph);
    }

    /**
     * Initialize values
     */
    public initialize() {
        // Poisson disk for Shadow Map
        for (let i = 0; i < 16; ++i) {
            this._poissonDisk[i] = vec2.fromValues(
                Math.random(),
                Math.random()
            );
        }

        let shaders = this._renderGraph.getLayer('shadowMaps').getShaders();
        this._shader = shaders.get(ShadowMapPoissonShader.TypeName);
        this.attachShaderToBin(OpaqueBinTypeName, this._shader);
        this.attachShaderToBin(OpaqueNoTextureBinTypeName, this._shader);
    }

    public render(aView: View, aOutput: Framebuffer, aDepthTexture: WebGLTexture) {
        /* jshint camelcase:false */
        this.view = aView;
        this.state.resetRenderNodesFilters();

        let gl = this._context.getWebGLRenderingContext();
        let scale = aOutput.getParameters().scale;
        let position = aView.getPosition();
        let size = aView.getSize();

        aOutput.bind();
        gl.enable(gl.SCISSOR_TEST);
        gl.viewport(position[0] * scale, position[1] * scale, size[0] * scale, size[1] * scale);
        gl.scissor(position[0] * scale, position[1] * scale, size[0] * scale, size[1] * scale);
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

        // bind shader
        let shader = this._shader.get(0);
        this.bindShader(OpaqueBinTypeName);
        this.state.setRenderablesFilters(this._activeShaderRenderable.getRenderablesFilters());

        // Textures
        // TODO gl.TEXTURE0 is not working: RENDER WARNING: there is no texture bound to the unit 0
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, aDepthTexture);
        gl.uniform1i(this._activeShaderRenderable.getUniformLocations().u_texture, 1);

        let uniformLocations = shader.getUniformLocations();

        gl.uniform1f(uniformLocations.u_texture_size, this._renderGraph.getLayer('shadowMaps').getTextureSize());

        // Uniforms
        gl.uniformMatrix4fv(uniformLocations.u_view_projection, false, aView.getCamera().getViewProjectionMatrix());
        gl.uniformMatrix4fv(uniformLocations.u_light_matrix, false, aView.getCamera().getLightOrthogonalMatrix());
        gl.uniform3fv(uniformLocations.u_light_pos, aView.getCamera().getLightComponents().light);

        // Poisson disk
        for (let pd = 0; pd < this._poissonDisk.length; ++pd) {
            let location = gl.getUniformLocation(shader.getProgram(), "u_poisson[" + pd + "]");
            gl.uniform2fv(location, this._poissonDisk[pd]);
        }

        // Draw
        let bins = this._renderGraph.getRenderBins();
        bins.get(OpaqueBinTypeName).render(this);
        bins.get(OpaqueNoTextureBinTypeName).render(this);

        // Reset
        shader.finishRender();
        this.view = null;
    }
}

Factory.register(Containers.PASSES_SM, ShadowMapPoisson);
